<script setup lang="ts">
import { useEventListener } from '@vueuse/core'

/**
 * Sticky bottom bar on blog articles: one line of copy and one button into
 * `/free-cv-builder`.
 *
 * Deliberately quiet about when it appears. It waits until the reader is a
 * quarter of the way down (a bar that greets you at the top of an article is
 * an ad, one that arrives mid-read is an offer), and it steps back out near
 * the bottom of the page, where `SofaxPageCta` already makes the same ask —
 * two stacked calls to action read as desperation.
 *
 * Dismissal is remembered for the session, so a reader who closes it can move
 * through the rest of the blog without being asked again.
 */
const props = defineProps<{
  /** Article slug, passed through to the GTM event so CTAs can be compared. */
  slug?: string
}>()

const { t } = useI18n()
const { isAuthenticated } = useAuthUser()
const { trackEvent } = useGTM()

/** Appear after this share of the page, retreat after the second one. */
const SHOW_AFTER = 0.25
const HIDE_AFTER = 0.9

const DISMISS_KEY = 'blog_cta_bar_dismissed'

/*
 * The cookie banner is pinned to the same edge and is full-width on phones.
 * Consent is the more urgent ask, so the bar waits until that card is gone.
 */
const cookieBannerVisible = useCookieBannerVisible()

const bar = ref<HTMLElement | null>(null)
const visible = ref(false)
const dismissed = ref(false)

/** Signed-in readers already have the builder; they get no pitch. */
const eligible = computed(() =>
  !isAuthenticated.value && !dismissed.value && !cookieBannerVisible.value)

function onScroll() {
  if (!eligible.value) {
    visible.value = false
    return
  }

  const doc = document.documentElement
  const scrollable = doc.scrollHeight - window.innerHeight
  // Article shorter than the viewport: nothing to scroll, nothing to reveal.
  if (scrollable <= 0) return

  const progress = window.scrollY / scrollable
  visible.value = progress >= SHOW_AFTER && progress < HIDE_AFTER
}

/* Accepting or declining cookies mid-read should let the bar in without a scroll. */
watch(eligible, () => onScroll())

function dismiss() {
  dismissed.value = true
  visible.value = false
  try {
    sessionStorage.setItem(DISMISS_KEY, 'true')
  } catch {
    // Not remembering the dismissal is survivable; failing the click is not.
  }
}

function onClick() {
  trackEvent('blog_cta_bar_click', { blog_slug: props.slug })
}

/*
 * The chat bubble and the open chat panel are pinned to the same corner. They
 * read this variable, so the bar lifts them by its real height instead of an
 * assumed one, and both land back where they were once the bar leaves.
 */
watch(visible, async value => {
  await nextTick()
  const height = value ? `${bar.value?.offsetHeight ?? 0}px` : '0px'
  document.documentElement.style.setProperty('--cta-bar-height', height)
})

onMounted(() => {
  try {
    dismissed.value = sessionStorage.getItem(DISMISS_KEY) === 'true'
  } catch {
    // Private mode, blocked storage: treat as "never dismissed".
  }
  onScroll()
})

onBeforeUnmount(() => {
  document.documentElement.style.setProperty('--cta-bar-height', '0px')
})

if (import.meta.client) {
  useEventListener(window, 'scroll', onScroll, { passive: true })
  useEventListener(window, 'resize', onScroll, { passive: true })
}
</script>

<template>
  <Transition
    enter-active-class="transition-transform duration-300 ease-out motion-reduce:transition-none"
    enter-from-class="translate-y-full"
    enter-to-class="translate-y-0"
    leave-active-class="transition-transform duration-200 ease-in motion-reduce:transition-none"
    leave-from-class="translate-y-0"
    leave-to-class="translate-y-full"
  >
    <!--
      z-40 keeps the bar under the chat widget and the cookie banner (z-50 and
      z-[60]): a promo must never swallow a control.
    -->
    <div
      v-if="visible"
      ref="bar"
      class="sofax fixed inset-x-0 bottom-0 z-40 border-t border-line bg-white/95 shadow-lift backdrop-blur dark:border-gray-800 dark:bg-gray-950/95"
    >
      <div class="shell flex items-center gap-4 py-3 sm:gap-6 sm:py-4">
        <span
          class="hidden size-12 shrink-0 items-center justify-center rounded-full bg-accent-soft text-xl text-ink sm:inline-flex"
          aria-hidden="true"
        >
          <i class="bi bi-file-earmark-text" />
        </span>

        <div class="min-w-0 flex-1">
          <!-- Two lines rather than an ellipsis: a truncated pitch persuades nobody. -->
          <p class="!mb-0 line-clamp-2 font-title !text-sm font-bold leading-snug text-ink sm:!text-lg dark:text-white">
            {{ t('blog.ctaBar.title') }}
          </p>
          <!-- The reasoning is desktop-only; on a phone the button has to carry it. -->
          <p class="!mb-0 hidden !text-sm text-muted md:block dark:text-gray-400">
            {{ t('blog.ctaBar.text') }}
          </p>
        </div>

        <SofaxBtn
          to="/free-cv-builder"
          :label="t('blog.ctaBar.button')"
          variant="accent"
          class="btn-sm shrink-0"
          @click="onClick"
        />

        <button
          type="button"
          class="shrink-0 p-1 text-xl leading-none text-muted transition-colors duration-300 hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-accent dark:text-gray-400 dark:hover:text-white"
          :aria-label="t('blog.ctaBar.dismiss')"
          :title="t('blog.ctaBar.dismiss')"
          @click="dismiss"
        >
          <i class="bi bi-x-lg" aria-hidden="true" />
        </button>
      </div>
    </div>
  </Transition>
</template>
